import { useEffect, useRef } from 'react';
import { SWIPE_CONFIG } from '../imageViewerUtils';

/**
 * Hook for swipe up/down to close functionality on touch devices
 * Image shrinks and follows finger (Photos-style)
 * 
 * @param {Object} options
 * @param {boolean} options.enabled - Whether swipe-to-close is enabled
 * @param {Function} options.onClose - Callback when swipe threshold is reached
 * @param {React.RefObject} options.figureRef - Ref to the figure element (touch target)
 * @param {React.RefObject} options.swipeWrapperRef - Ref to the wrapper element (transform target)
 */
export const useSwipeToClose = ({ enabled, onClose, figureRef, swipeWrapperRef }) => {
    const touchStartRef = useRef(null);
    const verticalLockRef = useRef(false);
    const animateBackTimeoutRef = useRef(null);
    
    useEffect(() => {
        if (!enabled) return;
        const el = figureRef.current;
        if (!el) return;
        
        const {
            MIN_VERTICAL,
            MIN_SWIPE_CLOSE,
            SCALE_DISTANCE,
            SCALE_MAX,
            ANIMATE_MS,
        } = SWIPE_CONFIG;

        const onTouchStart = (e) => {
            if (e.touches.length === 0) return;
            if (animateBackTimeoutRef.current) {
                clearTimeout(animateBackTimeoutRef.current);
                animateBackTimeoutRef.current = null;
            }
            if (swipeWrapperRef.current) {
                swipeWrapperRef.current.style.transition = '';
            }
            touchStartRef.current = { 
                y: e.touches[0].clientY, 
                x: e.touches[0].clientX 
            };
            verticalLockRef.current = false;
        };

        const onTouchMove = (e) => {
            if (!touchStartRef.current || e.touches.length === 0) return;
            const deltaY = e.touches[0].clientY - touchStartRef.current.y;
            const deltaX = e.touches[0].clientX - touchStartRef.current.x;
            
            if (!verticalLockRef.current) {
                if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > MIN_VERTICAL) {
                    verticalLockRef.current = true;
                    if (swipeWrapperRef.current) {
                        swipeWrapperRef.current.style.transition = '';
                    }
                } else {
                    return;
                }
            }
            
            if (verticalLockRef.current) {
                e.preventDefault();
                e.stopPropagation();
                const scale = 1 - Math.min(1, Math.abs(deltaY) / SCALE_DISTANCE) * SCALE_MAX;
                const opacity = Math.max(0.2, 1 - 0.8 * Math.min(1, Math.abs(deltaY) / SCALE_DISTANCE));
                
                if (swipeWrapperRef.current) {
                    const w = swipeWrapperRef.current;
                    w.style.transform = `translateY(${deltaY}px) scale(${scale})`;
                    w.style.opacity = String(opacity);
                }
            }
        };

        const onTouchEnd = (e) => {
            if (!verticalLockRef.current) return;
            const start = touchStartRef.current;
            if (!start || !e.changedTouches || e.changedTouches.length === 0) return;
            
            const deltaY = e.changedTouches[0].clientY - start.y;
            
            if (Math.abs(deltaY) >= MIN_SWIPE_CLOSE) {
                onClose();
            } else {
                const w = swipeWrapperRef.current;
                if (w) {
                    w.style.transition = `transform ${ANIMATE_MS}ms ease-out, opacity ${ANIMATE_MS}ms ease-out`;
                    w.style.transform = 'translateY(0) scale(1)';
                    w.style.opacity = '1';
                    animateBackTimeoutRef.current = setTimeout(() => {
                        animateBackTimeoutRef.current = null;
                        if (swipeWrapperRef.current) {
                            swipeWrapperRef.current.style.transition = '';
                        }
                    }, ANIMATE_MS);
                }
            }
            verticalLockRef.current = false;
            touchStartRef.current = null;
        };

        el.addEventListener('touchstart', onTouchStart, { capture: true });
        el.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
        el.addEventListener('touchend', onTouchEnd, { capture: true });
        
        return () => {
            el.removeEventListener('touchstart', onTouchStart, { capture: true });
            el.removeEventListener('touchmove', onTouchMove, { passive: false, capture: true });
            el.removeEventListener('touchend', onTouchEnd, { capture: true });
            if (animateBackTimeoutRef.current) {
                clearTimeout(animateBackTimeoutRef.current);
                animateBackTimeoutRef.current = null;
            }
        };
    }, [enabled, onClose, figureRef, swipeWrapperRef]);
};

export default useSwipeToClose;
